import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import * as api from "@/lib/api";
import { AuthContext } from "./useAuth";

function parseJWTUserId(token: string): string | null {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    return payload.sub || null;
  } catch {
    return null;
  }
}

const REFRESH_TOKEN_KEY = "x402stack_refresh_token";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // Loading only while a stored refresh token is being exchanged.
  const [isLoading, setIsLoading] = useState(() =>
    Boolean(localStorage.getItem(REFRESH_TOKEN_KEY)),
  );

  const userId = useMemo(
    () => (accessToken ? parseJWTUserId(accessToken) : null),
    [accessToken],
  );

  // Try to refresh on mount
  useEffect(() => {
    const stored = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!stored) return;
    api
      .refreshToken(stored)
      .then((res) => {
        setAccessToken(res.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, res.refreshToken);
      })
      .catch(() => {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const sendOtp = useCallback(async (email: string) => {
    await api.sendOtp(email);
  }, []);

  const login = useCallback(async (email: string, code: string) => {
    const res = await api.verifyOtp(email, code);
    setAccessToken(res.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, res.refreshToken);
  }, []);

  const logout = useCallback(async () => {
    const stored = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (stored && accessToken) {
      try {
        await api.signOut(stored, accessToken);
      } catch {
        // best effort
      }
    }
    setAccessToken(null);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }, [accessToken]);

  const getToken = useCallback(() => accessToken, [accessToken]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        userId,
        isAuthenticated: !!accessToken,
        isLoading,
        login,
        sendOtp,
        logout,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
